// Helpers for the "carry forward" affordance on the prompt artifact card.
//
// When the user is staring at step N's drafted prompt, they need a way to
// hand step N-1's output to the external tool. Two paths:
//   • Inline: append a delimited block to the prompt body so the whole thing
//     fits in one paste.
//   • Download: generate a .md file the user can attach in tools that support
//     file uploads (Claude, Gemini, NotebookLM, ChatGPT).
//
// We try to detect the format and choose a sensible file extension.

import type { StepState } from '@/lib/workflowStreamProtocol';

const MAX_INLINE_CHARS = 16_000; // ~4-5k tokens — most chat tools accept this comfortably

export interface CarryForwardSource {
  stepId: string;
  position: number;
  stepName: string;
  /** What we'll actually paste/attach. */
  text: string;
  /** Inferred MIME-ish hint. */
  format: 'csv' | 'json' | 'markdown' | 'plain';
  bytes: number;
  rows?: number;
  inlinable: boolean;
}

/** Build carry-forward sources from the workflow's prior step outputs. */
export function deriveCarryForwardSources(priorSteps: StepState[]): CarryForwardSource[] {
  return priorSteps
    .map(step => {
      const text = step.pastedOutput?.text;
      if (!text || !text.trim()) return null;
      const format = step.pastedOutput?.meta?.mimeHint ?? inferFormat(text);
      const bytes = step.pastedOutput?.meta?.bytes ?? new Blob([text]).size;
      const rows = step.pastedOutput?.meta?.rows;
      const source: CarryForwardSource = {
        stepId: step.id,
        position: step.position,
        stepName: step.stepName,
        text,
        format,
        bytes,
        rows,
        inlinable: text.length <= MAX_INLINE_CHARS,
      };
      return source;
    })
    .filter((s): s is CarryForwardSource => s !== null);
}

function inferFormat(text: string): CarryForwardSource['format'] {
  const head = text.trim().slice(0, 256);
  if (/^[[{]/.test(head)) return 'json';
  if (/^[^\s,]+(,[^\s,]+){2,}/.test(head)) return 'csv';
  if (/^#|^\*/.test(head)) return 'markdown';
  return 'plain';
}

/** Build the inline block that gets appended to a prompt body. */
export function buildInlineBlock(source: CarryForwardSource): string {
  const fence = source.format === 'csv' || source.format === 'json'
    ? source.format
    : '';
  const fenceOpen = '```' + fence;
  const fenceClose = '```';
  return `\n\n<!-- output from step ${source.position + 1}: ${source.stepName} -->\n${fenceOpen}\n${source.text.trim()}\n${fenceClose}`;
}

/** Build the .md file contents for a download. */
export function buildMarkdownFile(source: CarryForwardSource, workflowName: string | null): string {
  const header = `# Step ${source.position + 1} output — ${source.stepName}\n\n` +
    (workflowName ? `_From workflow: ${workflowName}_\n\n` : '') +
    `_Format: ${source.format}${source.rows ? ` · ${source.rows} rows` : ''} · ${formatBytes(source.bytes)}_\n\n---\n\n`;
  if (source.format === 'csv' || source.format === 'json') {
    return `${header}\`\`\`${source.format}\n${source.text.trim()}\n\`\`\`\n`;
  }
  return `${header}${source.text.trim()}\n`;
}

export function buildFileName(source: CarryForwardSource): string {
  const slug = source.stepName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
  const ext = source.format === 'csv' ? 'csv'
    : source.format === 'json' ? 'json'
    : 'md';
  return `step-${source.position + 1}-${slug || 'output'}.${ext}`;
}

/** Trigger a browser download for a string of text. */
export function downloadTextFile(filename: string, text: string, mime: string) {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so the download starts.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** MIME type that matches the file extension we'll download. */
export function mimeForFormat(format: CarryForwardSource['format']): string {
  switch (format) {
    case 'csv': return 'text/csv';
    case 'json': return 'application/json';
    case 'markdown': return 'text/markdown';
    case 'plain': return 'text/markdown';
  }
}
